import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import styles from "../../styles/styles";
import {
  AiFillHeart,
  AiOutlineHeart,
  AiOutlineMessage,
  AiOutlineShoppingCart,
} from "react-icons/ai";
import { MdOutlineLocalShipping } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { getAllProductsShop } from "../../redux/actions/product";
import { backend_url, server } from "../../server";
import {
  addToWishlist,
  removeFromWishlist,
} from "../../redux/actions/wishlist";
import { addTocart } from "../../redux/actions/cart";
import { toast } from "react-toastify";
import { fetchLiveProductStock } from "../../utils/productStock";
import axios from "axios";
import {
  bundlePostageDiscountLabel,
  bundlePostagePromoBadge,
  bundlePostagePromoSentence,
  isBundlePostagePromoEnabled,
} from "../../utils/checkoutPricing";
import {
  defaultBundlePostageConfig,
  estimateBundlePostageSavings,
  sumProductsPostage,
  computeBundleSelectionPricing,
} from "../../utils/bundlePricing";
import { useBuyerAcceptedOfferPrices } from "../../hooks/useBuyerAcceptedOfferPrices";
import {
  buildCartLineForAdd,
  getAcceptedOfferPriceFromOffer,
} from "../../utils/offerPricing";
import SocialProfileBadges from "../shared/SocialProfileBadges";
import BundleReviewModal from "./BundleReviewModal";
import MobileFriendlyModal, { MODAL_INPUT_CLASS, MODAL_INPUT_STYLE } from "../Layout/MobileFriendlyModal";
import {
  buildReturnToBundleUrl,
  clearBundleBuilderSession,
  getBundleBuilderSession,
  saveBundleBuilderSession,
} from "../../utils/bundleBuilderSession";

// Currency formatter for GBP
const formatCurrency = (value) => {
    if (value === null || value === undefined) return "£0.00";
    return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
    }).format(Number(value));
};

const isProductSoldOut = (product) => Number(product?.stock ?? 0) < 1;

/** Only rules the seller has turned on in their dashboard (active checkbox). */
const getActiveBundleRules = (shopInfo) => {
  const rules = shopInfo?.bundleRules;
  if (!Array.isArray(rules)) return [];
  return rules
    .filter((rule) => rule.active !== false)
    .sort((a, b) => Number(a.minItems || 0) - Number(b.minItems || 0));
};

const ProductDetails = ({ data }) => {
  const { products } = useSelector((state) => state.products);
  const { user, isAuthenticated } = useSelector((state) => state.user);
  const { wishlist } = useSelector((state) => state.wishlist);
  const { cart } = useSelector((state) => state.cart);
  const acceptedOfferPrices = useBuyerAcceptedOfferPrices(user?._id, isAuthenticated);
  const dispatch = useDispatch();

  const dispatchTimeDays = Number(data?.dispatchTimeDays ?? 1);
  const deliveryMethodKey = data?.deliveryMethod ?? "three_to_five_working_days";
  const deliveryMethod = (() => {
    const key = String(deliveryMethodKey || "").trim();
    if (key === "next_day") return { minDays: 1, maxDays: 1, label: "Next day" };
    if (key === "two_working_days") return { minDays: 2, maxDays: 2, label: "2 Working Days" };
    return { minDays: 3, maxDays: 5, label: "3-5 business working days" };
  })();

  const estimatedDeliveryMin = Math.max(1, dispatchTimeDays) + deliveryMethod.minDays;
  const estimatedDeliveryMax = Math.max(1, dispatchTimeDays) + deliveryMethod.maxDays;
  const estimatedDeliveryText =
    estimatedDeliveryMin === estimatedDeliveryMax
      ? `${estimatedDeliveryMin} Working Day${estimatedDeliveryMin === 1 ? "" : "s"}`
      : `${estimatedDeliveryMin}-${estimatedDeliveryMax} business working days`;

  // Check if user is the seller of this product
  const isOwnProduct = isAuthenticated && user && (
    String(user._id) === String(data?.shopId || data?.shop?._id)
  );

  const [count, setCount] = useState(1);
  const [click, setClick] = useState(false);
  const [select, setSelect] = useState(0);
  const [shopInfo, setShopInfo] = useState(null); // ✅ new state
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [offerPrice, setOfferPrice] = useState("");
  const [currentOffer, setCurrentOffer] = useState(null);
  const [isSubmittingOffer, setIsSubmittingOffer] = useState(false);
  const [bundleModalOpen, setBundleModalOpen] = useState(false);
  const [bundleReviewOpen, setBundleReviewOpen] = useState(false);
  const [bundleReviewSelection, setBundleReviewSelection] = useState([]);
  const [bundleOfferModalOpen, setBundleOfferModalOpen] = useState(false);
  const [bundleOfferSelection, setBundleOfferSelection] = useState([]);
  const [bundleOfferPrice, setBundleOfferPrice] = useState("");
  const [isSubmittingBundleOffer, setIsSubmittingBundleOffer] = useState(false);
  const [bundlePostageConfig, setBundlePostageConfig] = useState(defaultBundlePostageConfig);
  const [buyerProtectionConfig, setBuyerProtectionConfig] = useState({
    enabled: true,
    fixedFee: 0.7,
    percentage: 2,
  });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const showBundlePostagePromo = isBundlePostagePromoEnabled(bundlePostageConfig);
  const currentShopId = data?.shopId || data?.shop?._id;
  const shopProfilePath = currentShopId ? `/shop/${currentShopId}` : null;
  const shopDisplayName = shopInfo?.name || data?.shop?.name || "Seller";
  const shopAvatarPath = shopInfo?.avatar || data?.shop?.avatar;

  const openBundleModal = () => {
    const existing = getBundleBuilderSession();
    const sameShopSession =
      existing && String(existing.shopId) === String(currentShopId);
    const anchorProductId = sameShopSession ? existing.anchorProductId : data._id;
    const defaultSelection =
      data?._id && !isProductSoldOut(data) ? { [String(data._id)]: true } : {};
    const selection =
      sameShopSession &&
      existing.selection &&
      Object.keys(existing.selection).length > 0
        ? existing.selection
        : defaultSelection;

    saveBundleBuilderSession({
      anchorProductId,
      shopId: currentShopId,
      selection,
    });
    setBundleModalOpen(true);
  };

  const closeBundleModal = () => {
    clearBundleBuilderSession();
    setBundleModalOpen(false);
  };

  const bundleReturnSession = useMemo(() => {
    const session = getBundleBuilderSession();
    if (!session || !currentShopId) return null;
    if (String(session.shopId) !== String(currentShopId)) return null;
    if (String(session.anchorProductId) === String(data?._id)) return null;
    return session;
  }, [currentShopId, data?._id, bundleModalOpen, bundleReviewOpen]);

  useEffect(() => {
    if (searchParams.get("openBundle") !== "1" || !data?._id) return;

    const session = getBundleBuilderSession();
    if (
      !session ||
      String(session.anchorProductId) !== String(data._id) ||
      String(session.shopId) !== String(currentShopId)
    ) {
      return;
    }

    setBundleModalOpen(true);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("openBundle");
    setSearchParams(nextParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?._id, currentShopId]);

  const activeBundleRules = useMemo(
    () => getActiveBundleRules(shopInfo),
    [shopInfo?.bundleRules]
  );

  const hasBundleDiscountRules = activeBundleRules.length > 0;
  const showSellerOtherItems = !isOwnProduct && !!(data?.shopId || data?.shop?._id || shopInfo?._id);

  // ✅ Fetch latest shop info from backend (includes bundleRules)
  // Use shopId first (always correct), fallback to shop._id for backward compatibility
  const prevShopIdRef = React.useRef(null);
  const isFetchingShopInfoRef = React.useRef(false);
  
  useEffect(() => {
    const shopId = data?.shopId || data?.shop?._id;
    
    // Only fetch if shop ID changed and not already fetching
    if (!shopId || shopId === prevShopIdRef.current || isFetchingShopInfoRef.current) {
      return;
    }
    
    prevShopIdRef.current = shopId;
    isFetchingShopInfoRef.current = true;
    
    const fetchShopInfo = async () => {
      try {
        const res = await axios.get(`${server}/shop/get-shop-info/${shopId}`);
        setShopInfo(res.data.shop);
      } catch (error) {
        console.error("Failed to load shop info:", error);
      } finally {
        isFetchingShopInfoRef.current = false;
      }
    };
    
    fetchShopInfo();
  }, [data?.shopId, data?.shop?._id]);

  useEffect(() => {
    const loadPaymentSettings = async () => {
      try {
        const { data: settingsRes } = await axios.get(`${server}/options/public-payment-settings`);
        const bpd = settingsRes?.settings?.bundlePostageDiscount || {};
        setBundlePostageConfig({
          enabled: bpd.enabled !== false,
          percentage: Number(bpd.percentage ?? 30),
        });
        const bp = settingsRes?.settings?.buyerProtection || {};
        setBuyerProtectionConfig({
          enabled: bp.enabled !== false,
          fixedFee: Number(bp.fixedFee ?? 0.7),
          percentage: Number(bp.percentage ?? 2),
        });
      } catch {
        setBundlePostageConfig(defaultBundlePostageConfig);
        setBuyerProtectionConfig({
          enabled: true,
          fixedFee: 0.7,
          percentage: 2,
        });
      }
    };
    loadPaymentSettings();
  }, []);

  // Load existing offer for this user/product if logged in
  const prevProductIdRef = React.useRef(null);
  const prevIsAuthenticatedRef = React.useRef(null);
  const isFetchingOfferRef = React.useRef(false);
  
  useEffect(() => {
    const productId = data?._id;
    const wasAuthenticated = prevIsAuthenticatedRef.current;
    
    // Only fetch if product ID or auth status changed, and not already fetching
    if (!isAuthenticated || !productId || 
        (productId === prevProductIdRef.current && isAuthenticated === wasAuthenticated) ||
        isFetchingOfferRef.current) {
      prevProductIdRef.current = productId;
      prevIsAuthenticatedRef.current = isAuthenticated;
      return;
    }
    
    prevProductIdRef.current = productId;
    prevIsAuthenticatedRef.current = isAuthenticated;
    isFetchingOfferRef.current = true;
    
    const fetchOffer = async () => {
      try {
        const { data: resp } = await axios.get(`${server}/offer/my/${productId}`, {
          withCredentials: true,
        });
        // Normalize conversationId (handle both string and object)
        const offer = resp.offer || null;
        if (offer && offer.conversationId) {
          offer.conversationId = offer.conversationId?._id || offer.conversationId || null;
        }
        setCurrentOffer(offer);
      } catch (err) {
        console.error("Failed to load existing offer", err);
      } finally {
        isFetchingOfferRef.current = false;
      }
    };
    
    fetchOffer();
  }, [isAuthenticated, data?._id]);

  // Use ref to track previous shop ID for products fetch (reuse same ref since it's the same shop ID)
  const prevShopIdForProductsRef = React.useRef(null);
  const isFetchingProductsRef = React.useRef(false);
  
  useEffect(() => {
    const shopId = data && (data?.shopId || data?.shop?._id);
    
    // Only fetch if shop ID changed and not already fetching
    if (!shopId || shopId === prevShopIdForProductsRef.current || isFetchingProductsRef.current) {
      return;
    }
    
    prevShopIdForProductsRef.current = shopId;
    isFetchingProductsRef.current = true;
    
    dispatch(getAllProductsShop(shopId)).finally(() => {
      isFetchingProductsRef.current = false;
    });
    
    if (wishlist && wishlist.find((i) => i._id === data?._id)) {
      setClick(true);
    } else {
      setClick(false);
    }
  }, [data?.shopId, data?.shop?._id, wishlist, data?._id, dispatch]);

  const removeFromWishlistHandler = (data) => {
    setClick(!click);
    dispatch(removeFromWishlist(data));
  };

  const addToWishlistHandler = (data) => {
    if (isOwnProduct) {
      toast.error("You cannot add your own product to wishlist!");
      return;
    }
    setClick(!click);
    dispatch(addToWishlist(data));
  };

  const addToCartHandler = async (id, acceptedPrice = null) => {
    if (isOwnProduct) {
      toast.error("You cannot buy your own product!");
      return;
    }

    const live = await fetchLiveProductStock(id);
    if (!live.ok) {
      toast.error(live.message || "Product stock limited!");
      return;
    }

    const listingPrice = Number((live.product || data)?.discountPrice || 0);
    const acceptedUnitPrice =
      acceptedPrice && Number(acceptedPrice) > 0 ? Number(acceptedPrice) : null;
    const existing = cart?.find((i) => String(i._id) === String(id));
    const result = buildCartLineForAdd({
      product: live.product || data,
      stock: live.stock,
      qty: count,
      listingPrice,
      acceptedUnitPrice,
      existingItem: existing,
    });

    if (result.error === "stock") {
      toast.error(
        result.stock > 0
          ? `Only ${result.stock} available in stock.`
          : live.message || "Product stock limited!"
      );
      return;
    }
    if (result.error === "bundle_offer") {
      toast.error(
        "This item is in your cart as part of a bundle offer. Adjust quantity in the cart or remove it first."
      );
      return;
    }
    if (result.error === "different_price") {
      toast.error("This item is already in your cart at a different price.");
      return;
    }

    dispatch(addTocart(result.item));
    toast.success(
      result.merged
        ? `Updated to ${result.item.qty} at your accepted offer price.`
        : "Item added to cart successfully!"
    );
  };

  const handleBundleCheckout = (selectedProductIds = []) => {
    if (isOwnProduct) {
      toast.error("You cannot buy your own products!");
      return;
    }

    if (!Array.isArray(selectedProductIds) || selectedProductIds.length === 0) {
      toast.error("Please select at least one item for your bundle.");
      return;
    }

    const productsMap = new Map(products.map((p) => [p._id, p]));
    let addedCount = 0;

    selectedProductIds.forEach((pid) => {
      const product = productsMap.get(pid);
      if (!product) return;
      if (isProductSoldOut(product)) return;
      const exists = cart && cart.find((i) => i._id === pid);
      if (exists) return;

      const cartData = {
        ...product,
        qty: 1,
      };
      dispatch(addTocart(cartData));
      addedCount += 1;
    });

    if (addedCount === 0) {
      toast.error("Selected items are sold out or already in your cart.");
      return;
    }

    navigate("/checkout");
  };

  const resolveBundleProducts = (selectedProductIds = []) => {
    const productMap = new Map((products || []).map((p) => [String(p._id), p]));
    if (data?._id && !productMap.has(String(data._id))) {
      productMap.set(String(data._id), data);
    }
    return selectedProductIds
      .map((id) => productMap.get(String(id)))
      .filter(Boolean)
      .filter((product) => !isProductSoldOut(product));
  };

  const bundleReviewProducts = useMemo(
    () => resolveBundleProducts(bundleReviewSelection),
    [bundleReviewSelection, products, data]
  );

  const handleBundleReviewStart = (selectedProductIds = []) => {
    const selectedProducts = resolveBundleProducts(selectedProductIds);
    if (selectedProducts.length === 0) {
      toast.error("Please select at least one available item for your bundle.");
      return;
    }
    setBundleReviewSelection(selectedProducts.map((p) => p._id));
    setBundleModalOpen(false);
    setBundleReviewOpen(true);
  };

  const handleBundleReviewBack = () => {
    setBundleReviewOpen(false);
    setBundleModalOpen(true);
  };

  const handleBundleReviewBuyNow = () => {
    handleBundleCheckout(bundleReviewSelection);
    setBundleReviewOpen(false);
    setBundleReviewSelection([]);
  };

  const handleBundleReviewAskSeller = async () => {
    if (isOwnProduct) {
      toast.error("You cannot message yourself");
      return;
    }
    if (!isAuthenticated) {
      toast.error("Please login to message the seller");
      return;
    }
    await handleMessageSubmit();
    setBundleReviewOpen(false);
  };

  const handleBundleReviewMakeOffer = () => {
    handleBundleOfferStart(bundleReviewSelection);
    setBundleReviewOpen(false);
  };

  const handleBundleOfferStart = (selectedProductIds = []) => {
    if (!Array.isArray(selectedProductIds) || selectedProductIds.length < 2) {
      toast.error("Select at least 2 products to send a bundle offer.");
      return;
    }

    const productsMap = new Map(products.map((p) => [String(p._id), p]));
    const soldOutSelected = selectedProductIds.filter((pid) => {
      const product = productsMap.get(String(pid));
      return product && isProductSoldOut(product);
    });
    if (soldOutSelected.length > 0) {
      toast.error("Remove sold out items from your bundle before sending an offer.");
      return;
    }
    setBundleOfferSelection(selectedProductIds);
    setBundleOfferPrice("");
    setBundleOfferModalOpen(true);
  };

  const bundleOfferPricing = useMemo(() => {
    const selectedProducts = resolveBundleProducts(bundleOfferSelection);
    const pricing = computeBundleSelectionPricing({
      products: selectedProducts,
      bundleRules: shopInfo?.bundleRules,
      bundlePostageConfig,
      buyerProtectionConfig,
    });
    const itemsAndPostageTotal = Math.max(
      0,
      Number((pricing.subtotal - pricing.bundleDiscount + pricing.postageCharged).toFixed(2))
    );
    return {
      itemCount: pricing.itemCount,
      subtotal: pricing.subtotal,
      discountPercent: pricing.discountPercent,
      discountAmount: pricing.bundleDiscount,
      postageTotal: pricing.postageTotal,
      postageDiscount: pricing.postageDiscount,
      qualifiesForBundle: pricing.qualifiesForBundle,
      buyerProtectionFee: pricing.buyerProtectionFee,
      estimatedTotal: itemsAndPostageTotal,
      checkoutTotal: pricing.total,
    };
  }, [bundleOfferSelection, products, data, shopInfo?.bundleRules, bundlePostageConfig, buyerProtectionConfig]);

  const handleBundleOfferSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error("Please login to make an offer");
      return;
    }
    const numeric = Number(bundleOfferPrice);
    if (!numeric || numeric <= 0) {
      toast.error("Please enter a valid bundle offer price");
      return;
    }
    if (!Array.isArray(bundleOfferSelection) || bundleOfferSelection.length < 2) {
      toast.error("Please select at least 2 items.");
      return;
    }

    try {
      setIsSubmittingBundleOffer(true);
      const { data: resp } = await axios.post(
        `${server}/offer/create-bundle`,
        {
          productIds: bundleOfferSelection,
          price: numeric,
        },
        { withCredentials: true }
      );
      const conversationId =
        resp?.offer?.conversationId?._id ||
        resp?.offer?.conversationId ||
        null;
      toast.success("Your bundle offer has been sent to the seller");
      setBundleOfferModalOpen(false);
      setBundleOfferSelection([]);
      if (conversationId) {
        navigate(`/profile/inbox?conversation=${conversationId}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Unable to submit bundle offer");
    } finally {
      setIsSubmittingBundleOffer(false);
    }
  };

  const incrementCount = () => {
    const maxStock = Number(data?.stock || 0);
    setCount((prev) => (maxStock > 0 ? Math.min(prev + 1, maxStock) : prev + 1));
  };
  const decrementCount = () => {
    if (count > 1) setCount(count - 1);
  };

  const handleMessageSubmit = async () => {
    if (isAuthenticated) {
      // Prevent users from messaging themselves
      const sellerId = data?.shopId || data?.shop?._id;
      if (user._id && sellerId && String(user._id) === String(sellerId)) {
        toast.error("You cannot message yourself");
        return;
      }

      const groupTitle = `${sellerId}_${user._id}`;
      const userId = user._id;
      
      try {
        const res = await axios.post(
          `${server}/conversation/create-new-conversation`,
          {
            groupTitle,
            userId,
            sellerId,
            productId: data._id,
          }
        );

        const conversationId =
          res.data?.existingConversation?._id ||
          res.data?.conversation?._id ||
          res.data?._id;

        if (!conversationId) {
          throw new Error("Unable to determine conversation");
        }

        navigate(`/inbox?conversation=${conversationId}`);
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            "Unable to create conversation. Please try again."
        );
      }
    } else {
      toast.error("Please login to create a conversation");
    }
  };

  const handleOfferSubmit = async (e) => {
    e.preventDefault();
    if (isOwnProduct) {
      toast.error("You cannot make an offer on your own product!");
      return;
    }
    if (!isAuthenticated) {
      toast.error("Please login to make an offer");
      return;
    }
    const numeric = Number(offerPrice);
    if (!numeric || numeric <= 0) {
      toast.error("Please enter a valid price");
      return;
    }
    try {
      setIsSubmittingOffer(true);
      const { data: resp } = await axios.post(
        `${server}/offer/create`,
        {
          productId: data._id,
          price: numeric,
        },
        { withCredentials: true }
      );
      setCurrentOffer(resp.offer);
      toast.success("Your offer has been sent to the seller");
      setOfferModalOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Unable to submit offer");
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  const listingPrice = Number(data?.discountPrice || 0);
  const acceptedOfferPrice = useMemo(() => {
    const fromCurrent = getAcceptedOfferPriceFromOffer(
      currentOffer?.status === "accepted" ? currentOffer : null
    );
    if (fromCurrent) return fromCurrent;
    return acceptedOfferPrices[String(data?._id)] || null;
  }, [currentOffer, acceptedOfferPrices, data?._id]);
  const pendingCounterPrice =
    currentOffer?.status === "countered"
      ? Number(currentOffer.counterPrice || 0)
      : null;
  const displayPrice =
    acceptedOfferPrice && acceptedOfferPrice > 0 ? acceptedOfferPrice : listingPrice;
  const strikePrice =
    acceptedOfferPrice && acceptedOfferPrice > 0 && listingPrice > acceptedOfferPrice
      ? listingPrice
      : data?.originalPrice && data.originalPrice > listingPrice
        ? data.originalPrice
        : null;

  return (
    <div>
      {bundleReturnSession && !bundleModalOpen && !bundleReviewOpen ? (
        <div className="sticky top-0 z-[90] border-b border-emerald-200 bg-emerald-50 px-4 py-2.5">
          <div className="mx-auto flex w-[90%] max-w-5xl items-center justify-between gap-3 800px:w-[80%]">
            <p className="text-xs text-emerald-900 sm:text-sm">
              You are browsing items for your bundle.
            </p>
            <Link
              to={buildReturnToBundleUrl(bundleReturnSession.anchorProductId)}
              className="inline-flex shrink-0 items-center rounded-lg bg-[#38513b] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2f4232]"
            >
              Back to bundle
            </Link>
          </div>
        </div>
      ) : null}
      {data ? (
        <div className={`${styles.section} w-[90%] 800px:w-[80%] `}>
          <div className="w-full py-5">
            <div className="block w-full 800px:flex">
              {/* LEFT: IMAGES */}
              <div className="w-full 800px:w-[50%] main-prod-img">
                <img
                  src={
                    data?.images && data.images.length > 0
                      ? `${backend_url}${data.images[select]}`
                      : data?.image_Url?.[select]?.url
                  }
                  alt=""
                  className=""
                />
                <div className="w-full flex flex-wrap main-prod-img-g">
                  {data?.images?.map((i, index) => (
                    <div
                      key={index}
                      className={`${
                        select === index ? "border" : "null"
                      } cursor-pointer`}
                    >
                      <img
                        src={
                          data?.images && data.images.length > 0
                            ? `${backend_url}${i}`
                            : data?.image_Url?.[index]?.url
                        }
                        alt=""
                        className="overflow-hidden "
                        onClick={() => setSelect(index)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* RIGHT: DETAILS */}
              <div className="w-full 800px:w-[50%] pt-5 ">
                <h1 className={`${styles.productTitle}`}>{data.name}</h1>
                {/* <p>{data.description}</p> */}
                <div className="flex pt-3">
                  <h4 className={`${styles.productDiscountPrice}`}>
                    {formatCurrency(displayPrice)}
                  </h4>
                  {strikePrice && strikePrice > displayPrice && (
                    <h3 className={`${styles.price}`}>
                      {formatCurrency(strikePrice)}
                    </h3>
                  )}
                </div>
                {acceptedOfferPrice && acceptedOfferPrice > 0 && (
                  <p className="mt-1 text-sm font-medium text-emerald-700">
                    Accepted offer price — add to cart to buy at this price (use quantity above
                    for multiple items).
                  </p>
                )}
                {pendingCounterPrice > 0 && (
                  <p className="mt-1 text-sm text-sky-700">
                    Seller counter offer: {formatCurrency(pendingCounterPrice)} — accept in inbox
                    before this price applies.
                  </p>
                )}

                {/* eBay-style delivery estimate (dispatch + delivery method) */}
                <div className="mt-4 inline-flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#38513b]/10 text-[#38513b]">
                    <MdOutlineLocalShipping size={18} />
                  </span>
                  <div className="leading-tight">
                    <p className="text-sm font-semibold text-slate-700">
                      Estimated delivery
                    </p>
                    <p className="text-sm font-semibold text-[#38513b]">
                      {estimatedDeliveryText}
                    </p>
                  </div>
                </div>

                {/* Quantity & Wishlist */}
                <div className="flex items-center mt-12 justify-between pr-3">
                  <div>
                    <button
                      className="bg-[#38513b] text-white font-bold rounded-l px-4 py-2 shadow-lg hover:opacity-75 transition duration-300 ease-in-out"
                      onClick={decrementCount}
                    >
                      -
                    </button>

                    <span className="bg-gray-200 text-gray-800 font-medium px-4 py-[8px]">
                      {count}
                    </span>

                    <button
                      className="bg-[#38513b] text-white font-bold rounded-2 px-4 py-2 shadow-lg hover:opacity-75 transition duration-300 ease-in-out"
                      onClick={incrementCount}
                    >
                      +
                    </button>
                  </div>

                  <div>
                    {click ? (
                      <AiFillHeart
                        size={30}
                        className="cursor-pointer"
                        onClick={() => removeFromWishlistHandler(data)}
                        color="red"
                        title="Remove from wishlist"
                      />
                    ) : (
                      <AiOutlineHeart
                        size={30}
                        className="cursor-pointer"
                        onClick={() => addToWishlistHandler(data)}
                        title="Add to wishlist"
                      />
                    )}
                  </div>
                </div>

                {/* Purchase actions */}
                {isOwnProduct && (
                  <div className="mt-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800 font-medium">
                      ⚠️ You cannot buy your own product
                    </p>
                  </div>
                )}
                <div className="mt-6 flex flex-wrap gap-3">
                  {Number(data?.stock || 0) < 1 ? (
                    <p className="text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                      This item is sold out.
                    </p>
                  ) : null}
                  {/* Add to cart */}
                  <button
                    type="button"
                    className={`${styles.button} !rounded !h-11 flex items-center px-4 ${isOwnProduct || Number(data?.stock || 0) < 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => addToCartHandler(data._id, acceptedOfferPrice)}
                    disabled={isOwnProduct || Number(data?.stock || 0) < 1}
                  >
                    <span className="text-white flex items-center">
                      Add to Cart <AiOutlineShoppingCart className="ml-1" />
                    </span>
                  </button>

                  {/* Make an offer - only show if no active offer exists */}
                  {!currentOffer && (
                    <button
                      type="button"
                      className={`w-[200px] h-[50px] my-3 flex items-center justify-center rounded-xl cursor-pointer font-[600] !rounded !h-11 flex items-center px-4 text-white bg-[#38513b] ${isOwnProduct ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => {
                        if (isOwnProduct) {
                          toast.error("You cannot make an offer on your own product!");
                          return;
                        }
                        setOfferModalOpen(true);
                      }}
                      disabled={isOwnProduct}
                    >
                      Make an offer
                    </button>
                  )}
                  {currentOffer && (currentOffer.status === "pending" || currentOffer.status === "countered") && (
                    <Link
                      to={`/profile/inbox?conversation=${currentOffer?.conversationId?._id || currentOffer?.conversationId || ''}`}
                      className="w-[200px] h-[50px] my-3 flex items-center justify-center rounded-xl cursor-pointer font-[600] !rounded !h-11 flex items-center px-4 text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Continue in Inbox
                    </Link>
                  )}

                 
                  
                </div>

                {currentOffer && (
                  <div className="mt-2 space-y-2">
                    {acceptedOfferPrice > 0 && (
                      <p className="text-sm text-emerald-700">
                        You have an accepted offer for this product at{" "}
                        <span className="font-semibold">
                          {formatCurrency(acceptedOfferPrice)}
                        </span>
                        {" "}per item. Increase quantity above or add again to buy more at this
                        price.
                      </p>
                    )}
                    {pendingCounterPrice > 0 && (
                      <p className="text-sm text-sky-700 bg-sky-50 p-2 rounded-lg">
                        Seller countered with{" "}
                        <span className="font-semibold">
                          {formatCurrency(pendingCounterPrice)}
                        </span>
                        . Accept in your{" "}
                        <Link
                          to={`/profile/inbox?conversation=${currentOffer?.conversationId?._id || currentOffer?.conversationId || ""}`}
                          className="font-semibold underline hover:text-sky-900"
                        >
                          inbox
                        </Link>{" "}
                        before that price applies at checkout.
                      </p>
                    )}
                    {(currentOffer.status === "pending" || currentOffer.status === "countered") && (
                      <p className="text-sm text-blue-700 bg-blue-50 p-2 rounded-lg">
                        💬 Continue negotiating this offer in your{" "}
                        <Link to={`/profile/inbox?conversation=${currentOffer?.conversationId?._id || currentOffer?.conversationId || ''}`} className="font-semibold underline hover:text-blue-900">
                          inbox
                        </Link>
                        . All counter offers and negotiations happen there.
                      </p>
                    )}
                  </div>
                )}

                {/* View seller's other items — bundle picker (discounts optional) */}
                {showSellerOtherItems && (
                  <div
                    className={`mt-6 rounded-xl border-2 p-4 shadow-sm ${
                      hasBundleDiscountRules
                        ? "border-[#38513b]/20 bg-gradient-to-br from-emerald-50 to-green-50"
                        : "border-slate-200 bg-gradient-to-br from-slate-50 to-white"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-6 w-6 text-[#38513b]"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div className="flex-1">
                        {hasBundleDiscountRules ? (
                          <>
                            <h3 className="text-base font-semibold text-slate-900 mb-1">
                              Save More with Bundle Discounts! 🎁
                            </h3>
                            <p className="text-sm text-slate-600 mb-3">
                              Buy multiple items from this seller and enjoy exclusive bundle discounts:
                            </p>
                            <div className="flex flex-wrap gap-2 mb-3">
                              {activeBundleRules.map((rule, idx) => (
                                <div
                                  key={idx}
                                  className="inline-flex items-center gap-2 rounded-lg bg-white border border-emerald-200 px-3 py-1.5 shadow-sm"
                                >
                                  <span className="inline-flex h-6 items-center justify-center rounded-full bg-[#38513b] px-2.5 text-xs font-bold text-white">
                                    {rule.minItems}+ products
                                  </span>
                                  <span className="text-sm font-semibold text-emerald-700">
                                    {Number(rule.discountPercent || 0).toFixed(0)}% OFF
                                  </span>
                                </div>
                              ))}
                              {showBundlePostagePromo ? (
                                <div className="inline-flex items-center gap-2 rounded-lg bg-white border border-slate-200 px-3 py-1.5 shadow-sm">
                                  <span className="text-sm font-semibold text-slate-800">
                                    + {bundlePostagePromoBadge(bundlePostageConfig.percentage)}
                                  </span>
                                </div>
                              ) : null}
                            </div>
                            {showBundlePostagePromo ? (
                              <p className="text-sm text-slate-600 mb-3">
                                {bundlePostagePromoSentence(bundlePostageConfig.percentage)}
                              </p>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <h3 className="text-base font-semibold text-slate-900 mb-1">
                              More from this seller
                            </h3>
                            <p className="text-sm text-slate-600 mb-3">
                              See other items from this seller, add several to your cart in one go, or
                              send the seller a single offer for multiple products at once.
                            </p>
                          </>
                        )}
                        <button
                          type="button"
                          onClick={openBundleModal}
                          className="inline-flex items-center gap-2 rounded-lg bg-[#38513b] px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-[#2f4232] hover:shadow-lg"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                            />
                          </svg>
                          {hasBundleDiscountRules
                            ? "View seller's other items"
                            : "See more from this seller"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Shop Info */}
                <div className="flex flex-col gap-3 pt-8 sm:flex-row sm:flex-wrap sm:items-start">
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    {shopProfilePath ? (
                      <Link
                        to={shopProfilePath}
                        state={{ fromProduct: data?._id }}
                        className="shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#38513b]"
                        aria-label={`View ${shopDisplayName}'s seller profile`}
                      >
                        <img
                          src={`${backend_url}${shopAvatarPath || "default-avatar.png"}`}
                          alt=""
                          className="h-[50px] w-[50px] rounded-full object-cover ring-2 ring-slate-100 transition hover:ring-[#38513b]"
                          onError={(e) => {
                            e.target.src = `${backend_url}default-avatar.png`;
                          }}
                        />
                      </Link>
                    ) : (
                      <img
                        src={`${backend_url}${shopAvatarPath || "default-avatar.png"}`}
                        alt=""
                        className="h-[50px] w-[50px] shrink-0 rounded-full object-cover"
                      />
                    )}

                    <div className="min-w-0 pr-4">
                      {shopProfilePath ? (
                        <>
                          <Link
                            to={shopProfilePath}
                            state={{ fromProduct: data?._id }}
                            className={`${styles.shop_name} block pb-1 pt-1 hover:underline`}
                          >
                            {shopDisplayName}
                          </Link>
                          <Link
                            to={`${shopProfilePath}?tab=reviews`}
                            state={{ fromProduct: data?._id }}
                            className="block pb-2 text-[14px] text-slate-600 hover:text-[#38513b] hover:underline"
                          >
                            View seller reviews
                          </Link>
                          <Link
                            to={shopProfilePath}
                            state={{ fromProduct: data?._id }}
                            className="mb-2 inline-block text-xs font-medium text-[#38513b] hover:underline"
                          >
                            View seller profile →
                          </Link>
                          <SocialProfileBadges
                            profiles={
                              shopInfo?.linkedSocialProfiles ||
                              data?.shop?.linkedSocialProfiles
                            }
                            compact
                            className="mb-2"
                          />
                        </>
                      ) : (
                        <>
                          <h3 className={`${styles.shop_name} pb-1 pt-1`}>{shopDisplayName}</h3>
                          {shopProfilePath ? null : (
                            <Link
                              to={`/shop/${data?.shop?._id || data?.shopId}?tab=reviews`}
                              state={{ fromProduct: data?._id }}
                              className="block pb-3 text-[14px] text-slate-600 hover:text-[#38513b] hover:underline"
                            >
                              View seller reviews
                            </Link>
                          )}
                          <SocialProfileBadges
                            profiles={
                              shopInfo?.linkedSocialProfiles ||
                              data?.shop?.linkedSocialProfiles
                            }
                            compact
                            className="mb-2"
                          />
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-start gap-1">
                    <div
                      className={`${styles.button} bg-[#6443d1] mt-4 !rounded !h-11 ${isOwnProduct ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      onClick={isOwnProduct ? undefined : handleMessageSubmit}
                    >
                      <span className="text-white flex items-center">
                        Send Message <AiOutlineMessage className="ml-1" />
                      </span>
                    </div>
                    {isOwnProduct && (
                      <p className="text-xs text-amber-600">
                        You cannot message yourself
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Product Info Tabs */}
          <ProductDetailsInfo
            data={data}
          />
          <br />
          <br />
        </div>
      ) : null}
      {/* Offer modal */}
      {offerModalOpen && (
        <MobileFriendlyModal
          onClose={() => setOfferModalOpen(false)}
          zClass="z-[100010]"
          panelClassName="max-w-md"
        >
            <h3 className="text-lg font-semibold text-slate-900">Make an offer</h3>
            <p className="mt-1 text-sm text-slate-500">
              Enter the price you would like to offer for this item. The seller can accept,
              reject or send a counter offer.
            </p>
            {currentOffer && (
              <div className="mt-3 rounded-xl bg-slate-50 p-3 text-xs text-slate-600 space-y-1">
                <div className="flex justify-between">
                  <span>Latest offer status</span>
                  <span className="font-semibold capitalize">{currentOffer.status}</span>
                </div>
                {acceptedOfferPrice > 0 && (
                  <div className="flex justify-between">
                    <span>Accepted price</span>
                    <span className="font-semibold">
                      {formatCurrency(acceptedOfferPrice)}
                    </span>
                  </div>
                )}
                {pendingCounterPrice > 0 && (
                  <div className="flex justify-between">
                    <span>Seller counter</span>
                    <span className="font-semibold">
                      {formatCurrency(pendingCounterPrice)}
                    </span>
                  </div>
                )}
              </div>
            )}
            <form onSubmit={handleOfferSubmit} className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Your offer price (GBP)
                </label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  inputMode="decimal"
                  value={offerPrice}
                  onChange={(e) => setOfferPrice(e.target.value)}
                  className={MODAL_INPUT_CLASS}
                  style={MODAL_INPUT_STYLE}
                  placeholder={String(data.discountPrice || "")}
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  onClick={() => setOfferModalOpen(false)}
                  disabled={isSubmittingOffer}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-[#38513b] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2f4232] disabled:opacity-60"
                  disabled={isSubmittingOffer}
                >
                  {isSubmittingOffer ? "Sending..." : "Send offer"}
                </button>
              </div>
            </form>
        </MobileFriendlyModal>
      )}

      {/* Bundle modal */}
      {bundleModalOpen && (
        <BundleBuilderModal
          key={data?._id || "bundle-modal"}
          products={products}
          currentProduct={data}
          shopInfo={shopInfo}
          bundlePostageConfig={bundlePostageConfig}
          onClose={closeBundleModal}
          onReview={handleBundleReviewStart}
        />
      )}
      {bundleReviewOpen && (
        <BundleReviewModal
          products={bundleReviewProducts}
          bundleRules={shopInfo?.bundleRules}
          bundlePostageConfig={bundlePostageConfig}
          buyerProtectionConfig={buyerProtectionConfig}
          isOwnProduct={isOwnProduct}
          onBack={handleBundleReviewBack}
          onClose={() => {
            setBundleReviewOpen(false);
            setBundleReviewSelection([]);
          }}
          onBuyNow={handleBundleReviewBuyNow}
          onAskSeller={handleBundleReviewAskSeller}
          onMakeOffer={handleBundleReviewMakeOffer}
        />
      )}
      {bundleOfferModalOpen && (
        <MobileFriendlyModal
          onClose={() => setBundleOfferModalOpen(false)}
          zClass="z-[100010]"
          panelClassName="max-w-md"
        >
            <h3 className="text-lg font-semibold text-slate-900">Make a bundle offer</h3>
            <p className="mt-1 text-sm text-slate-500">
              Offer a total price for {bundleOfferSelection.length} selected items from this seller.
            </p>
            <form onSubmit={handleBundleOfferSubmit} className="mt-4 space-y-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs font-semibold text-slate-700">Bundle cost preview</p>
                <div className="mt-2 space-y-1 text-xs text-slate-600">
                  <p>
                    Items: <span className="font-medium text-slate-800">{bundleOfferPricing.itemCount}</span>
                  </p>
                  <p>
                    Current bundle subtotal:{" "}
                    <span className="font-medium text-slate-800">
                      {formatCurrency(bundleOfferPricing.subtotal)}
                    </span>
                  </p>
                  {bundleOfferPricing.discountPercent > 0 && (
                    <p>
                      Seller bundle discount ({bundleOfferPricing.discountPercent}%):{" "}
                      <span className="font-medium text-slate-800">
                        -{formatCurrency(bundleOfferPricing.discountAmount)}
                      </span>
                    </p>
                  )}
                  {bundleOfferPricing.postageTotal > 0 && (
                    <p>
                      Estimated postage:{" "}
                      <span className="font-medium text-slate-800">
                        {formatCurrency(bundleOfferPricing.postageTotal)}
                      </span>
                    </p>
                  )}
                  {bundleOfferPricing.postageDiscount > 0 && (
                    <p>
                      {bundlePostageDiscountLabel(bundlePostageConfig.percentage)}:{" "}
                      <span className="font-medium text-slate-800">
                        -{formatCurrency(bundleOfferPricing.postageDiscount)}
                      </span>
                    </p>
                  )}
                  <p>
                    Estimated total with current bundle rules:{" "}
                    <span className="font-semibold text-[#38513b]">
                      {formatCurrency(bundleOfferPricing.estimatedTotal)}
                    </span>
                  </p>
                  {bundleOfferPricing.buyerProtectionFee > 0 && (
                    <p className="text-slate-500">
                      Buyer protection (added at checkout):{" "}
                      <span className="font-medium text-slate-700">
                        +{formatCurrency(bundleOfferPricing.buyerProtectionFee)}
                      </span>
                      {" · "}
                      Approx. checkout total:{" "}
                      <span className="font-medium text-slate-700">
                        {formatCurrency(bundleOfferPricing.checkoutTotal)}
                      </span>
                    </p>
                  )}
                  {showBundlePostagePromo && !bundleOfferPricing.qualifiesForBundle ? (
                    <p className="text-slate-500">
                      {bundlePostagePromoSentence(bundlePostageConfig.percentage)}
                    </p>
                  ) : null}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Your bundle offer (GBP)
                </label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  inputMode="decimal"
                  value={bundleOfferPrice}
                  onChange={(e) => setBundleOfferPrice(e.target.value)}
                  className={MODAL_INPUT_CLASS}
                  style={MODAL_INPUT_STYLE}
                  placeholder="0.00"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  onClick={() => setBundleOfferModalOpen(false)}
                  disabled={isSubmittingBundleOffer}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-[#38513b] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2f4232] disabled:opacity-60"
                  disabled={isSubmittingBundleOffer}
                >
                  {isSubmittingBundleOffer ? "Sending..." : "Send bundle offer"}
                </button>
              </div>
            </form>
        </MobileFriendlyModal>
      )}
    </div>
  );
};

const BundleBuilderModal = ({
  products,
  currentProduct,
  shopInfo,
  bundlePostageConfig = defaultBundlePostageConfig,
  onClose,
  onReview,
}) => {
  const navigate = useNavigate();
  const anchorProductId = currentProduct?._id;
  const shopId = currentProduct?.shopId || currentProduct?.shop?._id;

  const [selection, setSelection] = useState(() => {
    const saved = getBundleBuilderSession();
    if (saved?.selection && String(saved.shopId) === String(shopId)) {
      return { ...saved.selection };
    }
    const id = currentProduct?._id;
    if (!id || isProductSoldOut(currentProduct)) return {};
    return { [String(id)]: true };
  });
  const shopProducts =
    products?.filter(
      (p) =>
        (p.shop?._id || p.shopId) === shopId && !isProductSoldOut(p)
    ) || [];

  const activeBundleRules = getActiveBundleRules(shopInfo);
  const hasActiveBundleDiscounts = activeBundleRules.length > 0;

  const persistSelection = (nextSelection) => {
    const saved = getBundleBuilderSession();
    const persistAnchor =
      saved && String(saved.shopId) === String(shopId)
        ? saved.anchorProductId
        : anchorProductId;
    saveBundleBuilderSession({
      anchorProductId: persistAnchor,
      shopId,
      selection: nextSelection,
    });
  };

  const toggleProduct = (id) => {
    const key = String(id);
    setSelection((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (!next[key]) delete next[key];
      persistSelection(next);
      return next;
    });
  };

  const handleViewProduct = (productId) => {
    persistSelection(selection);
    navigate(`/product/${productId}`);
  };

  const selectedProducts = shopProducts.filter((p) => selection[String(p._id)]);
  const totalItems = selectedProducts.length;
  const subtotal = selectedProducts.reduce(
    (sum, p) => sum + Number(p.discountPrice || 0),
    0
  );

  let appliedRule = null;
  if (hasActiveBundleDiscounts && totalItems > 0) {
    appliedRule = activeBundleRules
      .filter((r) => Number(r.minItems) <= totalItems)
      .sort((a, b) => Number(b.minItems) - Number(a.minItems))[0];
  }

  const discountAmount =
    appliedRule && subtotal > 0
      ? (subtotal * Number(appliedRule.discountPercent || 0)) / 100
      : 0;
  const postageTotal = sumProductsPostage(selectedProducts);
  const postageDiscount = estimateBundlePostageSavings(
    selectedProducts,
    Boolean(appliedRule),
    bundlePostageConfig
  );
  const showPostagePromo = isBundlePostagePromoEnabled(bundlePostageConfig);

  return (
    <div className="fixed inset-0 z-[999] flex items-end justify-center bg-black/40 sm:items-center">
      <div className="flex max-h-[88vh] w-full flex-col rounded-t-2xl bg-white shadow-xl sm:w-[95%] sm:max-w-2xl sm:rounded-2xl">
        {/* Header - Fixed */}
        <div className="flex-shrink-0 px-4 pb-2 pt-4 sm:p-5 sm:pb-3">
          <h3 className="text-base font-semibold text-slate-900">
            {hasActiveBundleDiscounts ? "Create a bundle & save" : "More from this seller"}
          </h3>
          <p className="mt-0.5 text-[11px] leading-snug text-slate-500 sm:text-xs">
            {hasActiveBundleDiscounts
              ? "Choose items from this seller. Tap a product for details, or use the button to add it to your bundle."
              : "Tap a product for details, or add items to your selection."}
          </p>

          {hasActiveBundleDiscounts && (
            <div className="mt-2 flex flex-wrap gap-1.5 rounded-xl bg-slate-50 p-2">
              {activeBundleRules.map((rule, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-700 sm:text-[11px]"
                >
                  <span className="inline-flex h-5 items-center justify-center rounded-full bg-emerald-100 px-1.5 text-[10px] font-semibold text-emerald-700">
                    {rule.minItems}+
                  </span>
                  <span>{Number(rule.discountPercent || 0).toFixed(0)}% off</span>
                </div>
              ))}
              {showPostagePromo ? (
                <div className="flex items-center rounded-lg border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-700 sm:text-[11px]">
                  <span>+ {bundlePostagePromoBadge(bundlePostageConfig.percentage)}</span>
                </div>
              ) : null}
            </div>
          )}
          {showPostagePromo && hasActiveBundleDiscounts ? (
            <p className="mt-1.5 text-[10px] text-slate-500 sm:text-[11px]">
              {bundlePostagePromoSentence(bundlePostageConfig.percentage)}
            </p>
          ) : null}
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-5">
          {shopProducts.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-center text-xs text-slate-600">
              No other available items from this seller right now.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 rounded-xl border border-slate-200 p-2 sm:grid-cols-3 sm:gap-2.5 sm:p-2.5">
            {shopProducts.map((p) => {
              const isSelected = !!selection[String(p._id)];
              const imageSrc =
                p.images && p.images.length > 0 ? `${backend_url}${p.images[0]}` : null;
                return (
                  <div
                    key={p._id}
                    className={`flex flex-col rounded-lg border p-1.5 shadow-sm transition sm:p-2 ${
                      isSelected
                        ? "border-[#38513b] ring-1 ring-[#38513b]/40"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleViewProduct(p._id)}
                      className="group flex flex-1 flex-col text-left"
                    >
                      {imageSrc ? (
                        <div className="relative mb-1 overflow-hidden rounded-md bg-slate-50">
                          <img
                            src={imageSrc}
                            alt={p.name}
                            className="h-16 w-full object-contain transition group-hover:opacity-90 sm:h-20"
                          />
                        </div>
                      ) : null}
                      <h4 className="line-clamp-2 text-[10px] font-medium leading-tight text-slate-900 sm:text-[11px]">
                        {p.name}
                      </h4>
                      <p className="mt-0.5 text-[10px] font-bold text-[#38513b] sm:text-[11px]">
                        {formatCurrency(Number(p.discountPrice || 0))}
                      </p>
                      <span className="mt-0.5 text-[9px] text-slate-500 underline decoration-slate-300 underline-offset-2 group-hover:text-[#38513b] sm:text-[10px]">
                        View details
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleProduct(p._id)}
                      className={`mt-1.5 inline-flex h-7 items-center justify-center rounded-md border px-1.5 text-[10px] font-semibold transition sm:h-8 sm:text-[11px] ${
                        isSelected
                          ? "border-[#38513b] bg-[#38513b] text-white"
                          : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {isSelected
                        ? "Remove"
                        : hasActiveBundleDiscounts
                        ? "Add"
                        : "Select"}
                    </button>
                  </div>
                );
            })}
          </div>
          )}

          <div className="mt-3 space-y-0.5 rounded-lg bg-slate-50 p-2.5 text-[11px] text-slate-700 sm:text-xs">
            <div className="flex justify-between">
              <span>Items selected</span>
              <span className="font-semibold">{totalItems}</span>
            </div>
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span className="font-semibold">
                {formatCurrency(subtotal)}
              </span>
            </div>
            {appliedRule ? (
              <>
                <div className="flex justify-between">
                  <span>Bundle discount</span>
                  <span className="font-semibold text-slate-800">
                    {Number(appliedRule.discountPercent).toFixed(1)}% (-
                    {formatCurrency(discountAmount)})
                  </span>
                </div>
                {postageTotal > 0 ? (
                  <div className="flex justify-between">
                    <span>Postage</span>
                    <span className="font-semibold">{formatCurrency(postageTotal)}</span>
                  </div>
                ) : null}
                {postageDiscount > 0 ? (
                  <div className="flex justify-between">
                    <span>{bundlePostageDiscountLabel(bundlePostageConfig.percentage)}</span>
                    <span className="font-semibold text-slate-800">
                      -{formatCurrency(postageDiscount)}
                    </span>
                  </div>
                ) : null}
                <div className="flex justify-between border-t border-slate-200 pt-1.5">
                  <span className="font-semibold">Approx. total</span>
                  <span className="font-bold text-[#38513b]">
                    {formatCurrency(subtotal - discountAmount + postageTotal - postageDiscount)}
                  </span>
                </div>
              </>
            ) : hasActiveBundleDiscounts ? (
              <p className="mt-1 text-[10px] text-slate-500">
                Select more items to unlock a bundle discount.
              </p>
            ) : null}
          </div>

          {hasActiveBundleDiscounts ? (
            <p className="mt-1.5 pb-1 text-[10px] text-slate-500">
              Discounts apply automatically at checkout when these items are in your cart.
            </p>
          ) : (
            <p className="mt-1.5 pb-1 text-[10px] text-slate-500">
              Add selections to cart or send a bundle offer for two or more items.
            </p>
          )}
        </div>

        {/* Footer with buttons - Fixed */}
        <div className="flex-shrink-0 rounded-b-2xl border-t border-slate-200 bg-white px-4 py-3 sm:p-5 sm:pt-3">
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 sm:px-4 sm:py-2 sm:text-sm"
              onClick={onClose}
            >
              Close
            </button>
            <button
              type="button"
              disabled={selectedProducts.length === 0}
              onClick={() => {
                if (selectedProducts.length === 0) return;
                const ids = selectedProducts.map((p) => p._id);
                if (typeof onReview === "function") {
                  onReview(ids);
                }
              }}
              className="rounded-lg bg-[#38513b] px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-[#2f4232] disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:py-2 sm:text-sm"
            >
              Review bundle
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductDetailsInfo = ({
  data,
}) => {
  return (
    <div className="bg-[#f5f6fb] px-3 800px:px-10 py-2 rounded">
      <div className="w-full flex prod-det-tabs border-b pt-10 pb-2">
        <div className="relative active">
          <h5 className="text-[#000] text-[18px] px-1 leading-5 font-[600] 800px:text-[20px]">
            Product Details
          </h5>
        </div>
      </div>

      <div
        className="py-2 text-[18px] leading-8 pb-10 whitespace-pre-line"
        dangerouslySetInnerHTML={{ __html: data.description || "" }}
      />
    </div>
  );
};

export default ProductDetails;